import * as THREE from 'three';
import { BRUNI } from '@somnhold/content';
import { WORLD_PER_HEX } from '../coords.ts';
import type { UnRender } from './ctx.ts';

/**
 * Bruni's own effects (forge arcs, cold cyan): the cannon's shot and the riveted bolt, the shells of his black mortar
 * going up and bursting, the arcs that seize the towers, their bolts at the heroes and their bursts, and the forge
 * turned landing (the hammer on the ground, a ring of arcs out to his reach).
 */
const K = WORLD_PER_HEX / 1000;
const GLOW = '#4fe0ff';
const ACCENT = '#22c4ff';
const PALE = '#c8f6ff';
const SOOT = '#1a2226';

export const bruniFx: UnRender = {
  look: { glow: GLOW, accent: ACCENT, pale: PALE, dark: '#0b1418', smoke: SOOT },
  clips: { bruniRivet: 'rivet', bruniMortar: 'mortar' },
  frame: (c, s, e, p, yaw, h) => {
    const u = e.un;
    if (!u) return;
    const beat = (s.tick + e.id) % 12;
    const fwd = new THREE.Vector3(Math.sin(yaw), 0, Math.cos(yaw));
    // His forge breathes: soot and a blue spark off the pipe on his back.
    if (beat === 0) {
      const pipe = p.clone().addScaledVector(fwd, -0.35).setY(p.y + h * 0.95);
      c.vfx.burst(pipe, { n: c.low ? 1 : 2, color: SOOT, speed: 0.01, up: 0.04, size: 0.45, life: 18, smoke: true, spread: 0.1 });
      c.vfx.burst(pipe, { n: 1, color: GLOW, speed: 0.01, up: 0.05, size: 0.06, life: 10, gravity: -0.002 });
    }
    if (e.anim === 'walk' && beat % 6 === 0) {
      const side = beat % 12 === 0 ? 1 : -1;
      c.vfx.ring(p.clone().add(new THREE.Vector3(fwd.z * side * 0.3, 0.02, -fwd.x * side * 0.3)), 0.05, 0.34, ACCENT, 12, 0.5);
    }
  },
  skill: (c, _s, _e, name, p, h) => {
    if (name !== 'bruniMortar') return false;
    // Three shells thrown high off the cannon, trailing soot.
    const top = p.clone().setY(p.y + h * 1.1);
    c.vfx.burst(top, { n: c.low ? 4 : 9, color: SOOT, speed: 0.04, up: 0.12, size: 0.5, life: 18, smoke: true, spread: 0.2 });
    c.vfx.burst(top, { n: c.low ? 3 : 6, color: PALE, speed: 0.03, up: 0.22, size: 0.12, life: 14, gravity: 0.006 });
    c.flare(top, GLOW, 2, 8);
    c.shake(0.12);
    return true;
  },
  beam: (c, kind, a, b) => {
    if (kind === 'bruniShot' || kind === 'bruniRivet') {
      // A bolt of the forge: a blue streak from the cannon, a flash at the muzzle, sparks where it bites.
      const big = kind === 'bruniRivet';
      const m = a.clone().setY(a.y + 0.5);
      c.vfx.beam(m, b, ACCENT, big ? 8 : 5, big ? 0.07 : 0.04, 0.15);
      c.vfx.beam(m, b, PALE, big ? 6 : 4, big ? 0.03 : 0.015, 0);
      c.flare(m, GLOW, big ? 2.2 : 1.2, 5);
      c.vfx.burst(b, { n: c.low ? 4 : big ? 14 : 7, color: PALE, speed: big ? 0.12 : 0.08, up: 0.06, size: 0.07, life: 8, gravity: 0.012 });
      if (big) {
        c.vfx.ring(b.clone().setY(b.y - 0.55), 0.15, 1.1, ACCENT, 9, 1);
        c.shake(0.15);
      }
      return true;
    }
    if (kind === 'bruniTurned') {
      // A tower he took shoots a hero: the bastion's own bolt, gone blue.
      c.vfx.beam(a.clone().setY(a.y + 0.4), b, GLOW, 5, 0.04, 0.12);
      c.vfx.beam(a.clone().setY(a.y + 0.4), b, PALE, 3, 0.015, 0);
      c.vfx.burst(b, { n: c.low ? 3 : 6, color: GLOW, speed: 0.07, up: 0.05, size: 0.07, life: 7, gravity: 0.01 });
      return true;
    }
    if (kind === 'bruniSeize') {
      // Arcs leap from his hammer to the tower he takes.
      const hi = a.clone().setY(a.y + 0.8);
      const top = b.clone().setY(b.y + 0.9);
      for (let i = 0; i < 3; i++) c.vfx.beam(hi, top, i ? PALE : ACCENT, 10 + i * 3, 0.05 - i * 0.012, 0.5);
      c.vfx.shaft(b.clone().setY(b.y - 0.55), top.clone().setY(top.y + 2.5), ACCENT, 14, 0.7);
      c.flare(top, GLOW, 3, 12);
      return true;
    }
    return false;
  },
  boom: (c, _s, e, kind, p, r) => {
    const low = c.low;
    if (kind === 'bruniBlast' || kind === 'bruniBurst') {
      // A shell of the black mortar (or a tower he let go) bursts: a ring of arcs, black soot, shards of iron.
      // Out to the blast's own line: `r` comes in world units (times K again it shrank to nothing).
      const R = r;
      const burst = kind === 'bruniBurst';
      c.vfx.ring(p.clone().setY(p.y + 0.05), 0.2, R, ACCENT, 10, 1);
      c.vfx.ring(p.clone().setY(p.y + 0.08), 0.1, R * 0.6, PALE, 8, 0.8);
      c.vfx.burst(p.clone().setY(p.y + 0.3), { n: low ? 6 : 16, color: SOOT, speed: 0.07, up: 0.06, size: 0.6, life: 20, smoke: true, spread: R * 0.4 });
      c.vfx.burst(p.clone().setY(p.y + 0.3), { n: low ? 6 : 16, color: PALE, speed: 0.12, up: 0.1, size: 0.08, life: 12, gravity: 0.014 });
      c.vfx.burst(p.clone().setY(p.y + 0.3), { n: low ? 3 : 8, color: '#3a3f47', speed: 0.1, up: 0.1, size: 0.12, life: 16, gravity: 0.016 });
      c.flare(p.clone().setY(p.y + 0.6), GLOW, burst ? 5 : 3, 10);
      if (burst) c.vfx.shaft(p, p.clone().setY(p.y + 4), ACCENT, 12, 0.8);
      c.shake(burst ? 0.4 : 0.22);
      return true;
    }
    if (kind === 'bruniRelease') {
      c.vfx.burst(p.clone().setY(p.y + 1), { n: low ? 4 : 10, color: PALE, speed: 0.06, up: 0.05, size: 0.07, life: 10, gravity: 0.01 });
      c.vfx.burst(p.clone().setY(p.y + 1), { n: low ? 1 : 3, color: SOOT, speed: 0.03, up: 0.04, size: 0.5, life: 16, smoke: true });
      return true;
    }
    if (kind !== 'bruniForge') return false;
    // The forge turned: the hammer comes down, a ring of arcs runs out over his reach.
    if (e) c.force(e.id, 'forge');
    const R = BRUNI.forge.range * K;
    c.vfx.ring(p.clone().setY(p.y + 0.06), 0.3, R * 0.5, ACCENT, 14, 1);
    c.vfx.ring(p.clone().setY(p.y + 0.1), 0.2, R * 0.25, PALE, 12, 0.9);
    c.vfx.burst(p.clone().setY(p.y + 0.3), { n: low ? 8 : 22, color: PALE, speed: 0.12, up: 0.1, size: 0.09, life: 14, gravity: 0.012 });
    c.vfx.burst(p.clone().setY(p.y + 0.3), { n: low ? 5 : 12, color: SOOT, speed: 0.06, up: 0.05, size: 0.7, life: 24, smoke: true, spread: 0.5 });
    c.flare(p.clone().setY(p.y + 0.8), GLOW, 6, 14);
    c.shake(0.7);
    return true;
  },
};
